import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import { memo, type ReactNode, useEffect, useState } from "react";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";

import type { Song } from "@utils/player";
import { formatDurationSeconds } from "@utils/time";

const QUEUE_LEADING_CONTROL_WIDTH = 40;
const QUEUE_ROW_WITH_LEADING_CONTROL_STYLE = { marginLeft: -8 } as const;

interface SetListEntryItemSWMProps {
  entry: {
    startSeconds: number;
    title: string;
    artist: string;
  };
  isActive: boolean;
  onSetListEntryPress?: (songQueueId: string, startSeconds: number) => void;
  songQueueId: string;
}

const SetListEntryItemSWM = memo(
  ({
    entry,
    isActive,
    onSetListEntryPress,
    songQueueId,
  }: SetListEntryItemSWMProps) => (
    <LPressable
      sentryEventName="queue_setlist_entry_press_swm"
      onPress={() => onSetListEntryPress?.(songQueueId, entry.startSeconds)}
    >
      <LBox
        alignItems="center"
        flexDirection="row"
        paddingHorizontal="s3"
        paddingVertical="s1"
      >
        <LText
          color={isActive ? "black" : "blackTranslucent50"}
          marginRight="s3"
          style={{ minWidth: 60 }}
          variant="bodyM2m"
        >
          {formatDurationSeconds(entry.startSeconds)}
        </LText>
        <LBox flex={1}>
          <LText
            color={isActive ? "black" : "blackTranslucent70"}
            numberOfLines={1}
            variant="bodyM2ml"
          >
            {entry.title}
          </LText>
          <LText color="blackTranslucent50" numberOfLines={1} variant="bodyM2m">
            {entry.artist}
          </LText>
        </LBox>
      </LBox>
    </LPressable>
  ),
);

SetListEntryItemSWM.displayName = "SetListEntryItemSWM";

type QueueSong = Song & { songQueueId: string };

interface SongItemSWMProps {
  activeSetlistIndex?: number;
  disabled?: boolean;
  dragHandle?: ReactNode;
  expanded?: boolean;
  isCurrentSong?: boolean;
  noBottomMargin?: boolean;
  onPlay?: (songQueueId: string) => void;
  onSetListEntryPress?: (songQueueId: string, startSeconds: number) => void;
  onSetListExpandedChange?: (expanded: boolean) => void;
  select: (songQueueId: string) => void;
  selected: boolean;
  showSongRow?: boolean;
  song: QueueSong | null;
}

export const SetListToggleSWM = memo(function SetListToggleSWM({
  expanded,
  onPress,
}: {
  expanded: boolean;
  onPress: () => void;
}) {
  const rotation = useSharedValue(expanded ? 180 : 0);
  const animatedIconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  useEffect(() => {
    rotation.set(withTiming(expanded ? 180 : 0, { duration: 200 }));
  }, [expanded, rotation]);

  return (
    <LPressable
      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      onPress={onPress}
      sentryEventName="queue_setlist_toggle_swm"
    >
      <LBox
        alignItems="center"
        height={32}
        justifyContent="center"
        width={32}
      >
        <Animated.View style={animatedIconStyle}>
          <LIcon
            colors={{ color: "blackTranslucent50" }}
            name="down_arrow"
            size={18}
          />
        </Animated.View>
      </LBox>
    </LPressable>
  );
});

const QueueSelectIndicatorSWM = ({ selected }: { selected: boolean }) => (
  <LBox
    alignItems="center"
    backgroundColor={selected ? "blackTranslucent70" : undefined}
    borderColor="blackTranslucent30"
    borderRadius="max"
    borderWidth={2}
    height={24}
    justifyContent="center"
    width={24}
  >
    {selected ? (
      <LBox backgroundColor="white" borderRadius="max" height={8} width={8} />
    ) : null}
  </LBox>
);

const SongSelectionCellSWM = ({
  isCurrentSong,
  select,
  selected,
  songQueueId,
}: {
  isCurrentSong: boolean;
  select: (songQueueId: string) => void;
  selected: boolean;
  songQueueId: string;
}) => (
  <LBox flexDirection="column" justifyContent="center">
    {!isCurrentSong ? (
      <LPressable
        accessibilityLabel="Select song"
        accessibilityRole="checkbox"
        accessibilityState={{ checked: selected }}
        sentryEventName="queue_song_selected_swm"
        onPress={() => select(songQueueId)}
      >
        <LBox alignSelf="center" marginLeft="s3">
          <QueueSelectIndicatorSWM selected={selected} />
        </LBox>
      </LPressable>
    ) : (
      <LBox marginLeft="s3" width={24} />
    )}
  </LBox>
);

const SongTextSWM = ({ song }: { song: QueueSong }) => (
  <>
    <LText color="black" mb="s1" numberOfLines={1} variant="bodyM2ml">
      {song.title}
    </LText>
    <LText color="blackTranslucent70" numberOfLines={1} variant="bodyM2m">
      {song.artists.map((artist) => artist.name).join(", ") ?? ""}
    </LText>
  </>
);

const PlainSongItemSWM = memo(function PlainSongItemSWM({
  dragHandle,
  isCurrentSong = false,
  noBottomMargin = false,
  onPlay,
  select,
  selected,
  song,
}: Omit<
  SongItemSWMProps,
  | "activeSetlistIndex"
  | "disabled"
  | "onSetListEntryPress"
  | "onSetListExpandedChange"
> & {
  song: QueueSong;
}) {
  return (
    <LBox mb={isCurrentSong || noBottomMargin ? "zero" : "s4"}>
      <LBox
        flexDirection="row"
        style={
          isCurrentSong || dragHandle
            ? QUEUE_ROW_WITH_LEADING_CONTROL_STYLE
            : undefined
        }
      >
        {isCurrentSong ? (
          <LBox width={QUEUE_LEADING_CONTROL_WIDTH} />
        ) : (
          dragHandle
        )}
        <LBox
          alignItems="center"
          flex={1}
          flexDirection="row"
          justifyContent="space-between"
        >
          <LPressable
            sentryEventName="queue_song_play_swm"
            onPress={() => onPlay?.(song.songQueueId)}
            style={{ flex: 1 }}
          >
            <SongTextSWM song={song} />
          </LPressable>
          <SongSelectionCellSWM
            isCurrentSong={isCurrentSong}
            select={select}
            selected={selected}
            songQueueId={song.songQueueId}
          />
        </LBox>
      </LBox>
    </LBox>
  );
});

const SetListSongItemSWM = memo(function SetListSongItemSWM({
  activeSetlistIndex,
  dragHandle,
  expanded: controlledExpanded,
  isCurrentSong = false,
  noBottomMargin = false,
  onPlay,
  onSetListEntryPress,
  onSetListExpandedChange,
  select,
  selected,
  showSongRow = true,
  song,
}: Omit<SongItemSWMProps, "disabled" | "song"> & {
  song: QueueSong & {
    setList: NonNullable<QueueSong["setList"]> & {
      entries: NonNullable<NonNullable<QueueSong["setList"]>["entries"]>;
    };
  };
}) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = controlledExpanded ?? localExpanded;
  const height = useSharedValue(0);
  const opacity = useSharedValue(0);

  const animatedContainerStyle = useAnimatedStyle(() => ({
    height: height.value,
    opacity: opacity.value,
    overflow: "hidden" as const,
  }));

  const resolvedActiveSetlistIndex =
    isCurrentSong && activeSetlistIndex !== undefined ? activeSetlistIndex : -1;

  useEffect(() => {
    const estimatedHeight = song.setList.entries.length * 40;
    height.set(
      withTiming(expanded ? estimatedHeight : 0, {
        duration: 200,
      }),
    );
    opacity.set(withTiming(expanded ? 1 : 0, { duration: 200 }));
  }, [expanded, height, opacity, song.setList.entries.length]);

  const toggleExpanded = () => {
    const newExpanded = !expanded;
    setLocalExpanded(newExpanded);
    onSetListExpandedChange?.(newExpanded);
  };

  return (
    <LBox mb={isCurrentSong || noBottomMargin ? "zero" : "s4"}>
      {showSongRow ? (
        <LBox
          flexDirection="row"
          style={
            isCurrentSong || dragHandle
              ? QUEUE_ROW_WITH_LEADING_CONTROL_STYLE
              : undefined
          }
        >
          {isCurrentSong ? (
            <LBox width={QUEUE_LEADING_CONTROL_WIDTH} />
          ) : (
            dragHandle
          )}
          <LBox
            alignItems="center"
            flex={1}
            flexDirection="row"
            justifyContent="space-between"
          >
            <LPressable
              sentryEventName="queue_song_play_swm"
              onPress={() => onPlay?.(song.songQueueId)}
              style={{ flex: 1, maxWidth: "75%" }}
            >
              <SongTextSWM song={song} />
            </LPressable>
            <LBox marginRight="s2">
              <SetListToggleSWM
                expanded={expanded}
                onPress={toggleExpanded}
              />
            </LBox>
            <SongSelectionCellSWM
              isCurrentSong={isCurrentSong}
              select={select}
              selected={selected}
              songQueueId={song.songQueueId}
            />
          </LBox>
        </LBox>
      ) : null}
      <Animated.View style={animatedContainerStyle}>
        <LBox marginLeft="m1" marginTop="s1">
          {song.setList.entries.map((entry, index) => (
            <SetListEntryItemSWM
              key={index}
              entry={entry}
              isActive={resolvedActiveSetlistIndex === index}
              onSetListEntryPress={onSetListEntryPress}
              songQueueId={song.songQueueId}
            />
          ))}
        </LBox>
      </Animated.View>
    </LBox>
  );
});

const SongItemSWMComponent = (props: SongItemSWMProps) => {
  const { song } = props;

  if (!song) {
    return null;
  }

  const hasSetList =
    song.isSetList && song.setList?.entries && song.setList.entries.length > 0;

  if (hasSetList) {
    return (
      <SetListSongItemSWM
        {...props}
        song={
          song as QueueSong & {
            setList: NonNullable<QueueSong["setList"]> & {
              entries: NonNullable<
                NonNullable<QueueSong["setList"]>["entries"]
              >;
            };
          }
        }
      />
    );
  }

  return <PlainSongItemSWM {...props} song={song} />;
};

export const SongItemSWM = memo(SongItemSWMComponent);

interface SongRenderItemSWMProps {
  dragHandle?: ReactNode;
  isCurrentSong?: boolean;
  item: QueueSong;
  onPlay?: (songQueueId: string) => void;
  onSetListEntryPress?: (songQueueId: string, startSeconds: number) => void;
  onSetListExpandedChange?: (expanded: boolean) => void;
  select: (songQueueId: string) => void;
  selected: boolean;
}

const SongRenderItemSWMComponent = ({
  dragHandle,
  isCurrentSong = false,
  item,
  onPlay,
  onSetListEntryPress,
  onSetListExpandedChange,
  select,
  selected,
}: SongRenderItemSWMProps) => (
  <SongItemSWM
    dragHandle={dragHandle}
    isCurrentSong={isCurrentSong}
    onPlay={onPlay}
    onSetListEntryPress={onSetListEntryPress}
    onSetListExpandedChange={onSetListExpandedChange}
    select={select}
    selected={selected}
    song={item}
  />
);

export const SongRenderItemSWM = memo(SongRenderItemSWMComponent);
